/**
 * Runtime configuration. One image serves every environment: `docker-entrypoint.sh` writes
 * `/config.json` from the container's environment (PARS_URL, A3S_URL, APP_PROFILE) and
 * `loadConfig()` applies it over the build defaults before the app renders. Locally the
 * REACT_APP_* variables in `.env` supply the defaults and `public/config.json` is an empty object.
 *
 * Read the values at call time (`Config.parsUrl`), never copy them at module load: the object is
 * filled in asynchronously.
 */
const stripSlash = (url) => (url && url.endsWith('/') ? url.slice(0, -1) : url);

const defaults = {
    parsUrl: process.env.REACT_APP_PARS_URL || 'http://localhost:8111',
    a3sUrl: process.env.REACT_APP_A3S_URL || 'http://localhost:9090',
    profile: process.env.REACT_APP_PROFILE || process.env.NODE_ENV || 'local',
    // the a3s web client; its secret is public by design (a browser cannot keep one)
    clientId: 'webAuthClient',
    clientSecret: 'gL%2TaqEGQCBe9UP',
};

export const Config = { ...defaults };

/** What is wrong with the runtime configuration of a deployed build, or null when it can run. */
export let configProblem = null;

/** Copies the string values of `json` over the defaults into `Config`. Unknown keys are ignored. */
export function applyConfig(json) {
    Object.keys(defaults).forEach((key) => {
        const value = json[key];
        if (typeof value === 'string' && value !== '') {
            Config[key] = value;
        }
    });
    Config.parsUrl = stripSlash(Config.parsUrl);
    Config.a3sUrl = stripSlash(Config.a3sUrl);
    return Config;
}

/** The keys a deployed container must provide (docker-entrypoint.sh writes them from PARS_URL and A3S_URL). */
export function productionProblem(json) {
    if (!json) return 'config.json is missing';
    const missing = ['parsUrl', 'a3sUrl'].filter((k) => typeof json[k] !== 'string' || !json[k].trim());
    return missing.length
        ? `config.json has no ${missing.join(' and ')}: set PARS_URL and A3S_URL on the container`
        : null;
}

/**
 * Applies `/config.json` over the build defaults. In a production build a missing or unreadable
 * file, or one without the two backend URLs, is a configuration failure: falling back to the
 * localhost defaults would silently serve a broken panel. Development keeps the build defaults.
 */
export async function loadConfig(production = process.env.NODE_ENV === 'production') {
    configProblem = null;
    let json = null;
    try {
        const response = await fetch('/config.json', { cache: 'no-store' });
        if (response.ok) json = await response.json();
        else if (production) configProblem = `config.json answered ${response.status}`;
    } catch (e) {
        if (production) configProblem = `config.json could not be read: ${e.message}`;
    }
    if (json) applyConfig(json);
    if (production && !configProblem) configProblem = productionProblem(json);
    return Config;
}
